import { ApolloError } from '@apollo/client'
import { FC, useEffect, useMemo } from 'react'

import { FetchKitQuery } from '../../../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { ContentfulPageTestimonialSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageTestimonialSection.client'
import { RenderOnceInViewport } from '../../../../app/_components/performance/RenderOnceInViewport.client'
import { NotFoundPage } from '../../../../app/_not-found.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { scrubTypeCategories } from '../../../../constants/figs'
import { Kit } from '../../../../types/graphql'
import { Product, ProductAsset } from '../../../../types/graphql'
import { PageSection } from '../../../page-sections/PageSection'
import { ProductGroupCategory } from '../Breadcrumbs/constants'
import { ProductPageBreadcrumbs } from '../Breadcrumbs/ProductPageBreadcrumbs'
import { GalleryImageStateContainer } from '../ProductPage/contexts/GalleryImageStateContainer'
import { FigsScrubsAreSection } from '../ProductPage/FigsScrubsAreSection/FigsScrubsAreSection'
import { ImageZoom } from '../ProductPage/ImageZoom'
import { ShopTheLookSection } from '../ProductPage/ShopTheLookSection/ShopTheLookSection'
import * as ProductPageStyles from '../styles'

import { KitDetails } from './KitDetails'
import { KitSwiper } from './KitSwiper'

export interface KitPageMainProps
  extends Readonly<{
    errors: ReadonlyArray<Error | ApolloError>
    handle: string
    kit: Kit
    kitContent: FetchKitQuery['kit']
  }> {}

export const KitPageMain: FC<KitPageMainProps> = ({ errors, handle, kitContent, kit }) => {
  //check if this is an empty kit (no productGroups in the collection)
  const isEmptyKit = !kit.collection?.productGroups?.length
  const isUnavailableKitProduct = !kit.productGroup?.products.length

  const kitToggles = useMemo(() => kitContent?.kitToggles ?? [], [kitContent?.kitToggles])

  //check that the productGroups in the collection have necessary fields
  const hasInvalidProductGroups = kit.collection?.productGroups?.some(productGroup => {
    return !productGroup.products.length
  })

  const hasErrors = errors.length > 0

  const didQueryFail: boolean =
    hasErrors || isEmptyKit || isUnavailableKitProduct || hasInvalidProductGroups

  useEffect(() => {
    if (!didQueryFail || !hasErrors) return
    reportClientError({
      error: errors?.[0],
      context: {
        scope: 'Kit Page',
        label: 'kit query failed',
      },
    })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didQueryFail, errors, handle])

  const splitColors = !(kitContent?.groupItemsByColor ?? true)

  const kitCollectionItems = useMemo(() => {
    return kit.collection.productGroups ?? []
  }, [kit.collection.productGroups])

  const kitItems = useMemo(() => {
    return splitColors
      ? kitCollectionItems.flatMap(productGroup => {
          //chunk the products array into arrays for each color, but keep different fits together
          const productsGroupedByColor = productGroup.products.reduce<Record<string, Product[]>>(
            (accumulator, product) => {
              const key: string = `${product.colorInfo?.rawName}`
              if (!accumulator[key]) accumulator[key] = []
              accumulator[key]?.push(product)
              return accumulator
            },
            {}
          )
          //convert each new color array into it's own productGroup so it can be a unique item in the kit
          return Object.values(productsGroupedByColor).map(productByColor => ({
            ...productGroup,
            colors: productGroup.colors.filter(
              colorDetail => colorDetail.rawName === productByColor[0]?.colorInfo?.rawName
            ),
            products: [...productByColor],
          }))
        })
      : kitCollectionItems
  }, [kitCollectionItems, splitColors])

  const kitProduct = kit.productGroup?.products[0]

  const firstItem = kitItems[0] ?? null

  const images = useMemo(() => {
    return kitProduct?.images?.nodes ?? []
  }, [kitProduct?.images?.nodes])

  const defaultColors = useMemo(() => {
    try {
      const parsedDefaultColors = JSON.parse(kitContent?.defaultColors || '', (key, val) =>
        typeof val === 'string' || !key ? val : ''
      )

      // make defaultColors object keys lowercased
      return Object.fromEntries(
        Object.entries(parsedDefaultColors).map(([key, value]) => [key.toLowerCase(), value])
      ) as Record<string, string>
    } catch (_e) {}

    return {}
  }, [kitContent?.defaultColors])

  const galleryImageList: ReadonlyArray<ProductAsset> = images.filter(o => o.kind !== 'wwlt') || []
  //sidebar with list of object in kit
  const kitDetailsSection = useMemo(() => {
    return !isEmptyKit ? (
      <KitDetails
        kitProductGroup={kit.productGroup}
        kitItemList={kitItems}
        defaultColors={defaultColors}
        hideIndividualProductDetailsModals={kitContent?.hideProductDetails ?? false}
        accordion={kitContent?.accordion ?? undefined}
        kitToggles={kitToggles}
        titleLockupImage={kitContent?.titleLockupImage ?? undefined}
        promotionalColoredTextSegments={kitContent?.promotionalColoredTextSegments ?? []}
      />
    ) : null
  }, [
    defaultColors,
    isEmptyKit,
    kit.productGroup,
    kitContent?.accordion,
    kitContent?.hideProductDetails,
    kitItems,
    kitToggles,
    kitContent?.titleLockupImage,
    kitContent?.promotionalColoredTextSegments,
  ])

  const kitSwiperBannerSection = useMemo(() => {
    return kit ? <KitSwiper kit={kit} /> : null
  }, [kit])
  const breadcrumbCategory = useMemo(() => {
    const categories = kit.collection.productGroups.map(pg => pg.rawCategory)
    const firstCategory = categories[0]
    const allSame = categories.every(category => category === firstCategory)
    return allSame ? firstCategory : ProductGroupCategory.Kit
  }, [kit.collection.productGroups])

  if (didQueryFail) return <NotFoundPage />

  return (
    <GalleryImageStateContainer.Provider initialState={{ images: galleryImageList }}>
      <TrackPageView pageViewHandle={kit.collection.name} pageViewCategory='kit' />
      <ProductPageStyles.Main>
        <ImageZoom />
        <ProductPageBreadcrumbs
          genderCategory={kit.collection.productGroups[0]?.genderCategory}
          category={breadcrumbCategory}
        />
        <ProductPageStyles.PrimaryAndAsideSection>
          <ProductPageStyles.PrimaryColumnDiv>
            {kitSwiperBannerSection}
          </ProductPageStyles.PrimaryColumnDiv>

          <ProductPageStyles.AsideColumnDiv>{kitDetailsSection}</ProductPageStyles.AsideColumnDiv>
        </ProductPageStyles.PrimaryAndAsideSection>

        {kitContent?.sections.length ? (
          <KitPageContentfulSections sections={kitContent.sections} />
        ) : null}

        {kitContent?.testimonials.length ? (
          <section>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <ContentfulPageTestimonialSection
                data={{
                  __typename: 'PageTestimonialSection',
                  id: 'kitContent',
                  title: kitContent.testimonialsTitle ?? '',
                  subtitle: kitContent.testimonialsSubtitle ?? '',
                  testimonials: kitContent.testimonials ?? [],
                }}
                backgroundColor='#f3f3f3'
              />
            </RenderOnceInViewport>
          </section>
        ) : null}

        <RenderOnceInViewport offset={{ bottom: 200 }}>
          <ProductPageStyles.Recommendations>
            <ShopTheLookSection handle={handle} wrapper={<section />} isKit />
          </ProductPageStyles.Recommendations>
        </RenderOnceInViewport>

        {scrubTypeCategories.includes(firstItem?.rawCategory || '') ? (
          <section>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <FigsScrubsAreSection />
            </RenderOnceInViewport>
          </section>
        ) : null}

        {/* This has not been working for awhile. Looking at its code, it has a ton of things that are obviously not valid for kit data model. We need to create a custom KitSchemaTags component with logic specific to kit data model. */}
        {/* kit.productGroup && canonicalUrl ? (
          <PdpSchemaTags
            productGroup={kit.productGroup}
            canonicalUrl={canonicalUrl}
            product={kitProduct}
          />
        ) : null */}
      </ProductPageStyles.Main>
    </GalleryImageStateContainer.Provider>
  )
}

const KitPageContentfulSections = ({
  sections,
}: {
  sections: NonNullable<FetchKitQuery['kit']>['sections']
}) => {
  if (sections.length < 1) return null

  return (
    <>
      {sections.map((section, index) => {
        const key = `${section.id}-${index}`
        return (
          <ProductPageStyles.Section key={key}>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <PageSection key={key} sectionModel={section} index={index + 1} />
            </RenderOnceInViewport>
          </ProductPageStyles.Section>
        )
      })}
    </>
  )
}
